import { useState } from 'react';

export interface PrescriptionFormData {
  medecin_id: string;
  date_prescription: string;
  diagnostic: string;
  medicaments: string;
  instructions: string;
  duree_traitement: string;
}

interface Medecin {
  id: string;
  nom: string;
  prenom: string;
  specialite: string;
}

interface PrescriptionFormProps {
  medecins: Medecin[];
  onSubmit: (data: PrescriptionFormData) => void;
  onCancel: () => void;
  loading: boolean;
}

export default function PrescriptionForm({ medecins, onSubmit, onCancel, loading }: PrescriptionFormProps) {
  const [form, setForm] = useState<PrescriptionFormData>({
    medecin_id: '',
    date_prescription: '',
    diagnostic: '',
    medicaments: '',
    instructions: '',
    duree_traitement: '',
  });

  const update = (field: keyof PrescriptionFormData, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.date_prescription || !form.medecin_id) {
      alert('Veuillez remplir la date et sélectionner un médecin.');
      return;
    }
    onSubmit(form);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Date de prescription *</label>
          <input
            type="date"
            required
            value={form.date_prescription}
            onChange={(e) => update('date_prescription', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-700 mb-1">Médecin prescripteur *</label>
          <select
            required
            value={form.medecin_id}
            onChange={(e) => update('medecin_id', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm appearance-none bg-white pr-8"
          >
            <option value="">Sélectionner un médecin...</option>
            {medecins.map((m) => (
              <option key={m.id} value={m.id}>
                Dr. {m.nom} {m.prenom} - {m.specialite}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Diagnostic</label>
        <textarea
          value={form.diagnostic}
          onChange={(e) => update('diagnostic', e.target.value)}
          rows={2}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
          placeholder="Diagnostic associé à cette prescription..."
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-700 mb-1">Médicaments prescrits</label>
        <textarea
          value={form.medicaments}
          onChange={(e) => update('medicaments', e.target.value)}
          rows={3}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
          placeholder="Liste des médicaments, posologie..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Instructions</label>
          <textarea
            value={form.instructions}
            onChange={(e) => update('instructions', e.target.value)}
            rows={2}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm resize-none"
            placeholder="Instructions particulières..."
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Durée du traitement</label>
          <input
            type="text"
            value={form.duree_traitement}
            onChange={(e) => update('duree_traitement', e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:border-clinic-blue text-sm"
            placeholder="Ex: 7 jours, 1 mois..."
          />
        </div>
      </div>

      <div className="flex gap-3 justify-end pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-medium text-sm whitespace-nowrap cursor-pointer"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-5 py-2.5 bg-clinic-blue-dark text-white rounded-xl hover:bg-clinic-blue transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer flex items-center gap-2"
        >
          {loading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Enregistrement...
            </>
          ) : (
            <>
              <i className="ri-save-line"></i>
              Enregistrer la prescription
            </>
          )}
        </button>
      </div>
    </form>
  );
}